import { filterProducts, getQuery } from '@web-app/helpers';
import { useCMSPage } from '@web-app/hooks/use-cms-page';
import { PropsShop } from '@web-app/view/product/interface-product';
import { InferGetServerSidePropsType } from 'next';

import { slugToMetal } from '@core/constant';
import { formatAlternates, formatMetas, mapSanityBreadcrumb } from '@core/sanity/utils';
import { EMetalSlug, ERouting, ShopQueryRaw } from '@core/type';
import { EQueryKey, DynamicSEOEntity } from '@core/type/api';
import { FilterProductsResult } from '@core/type/interface/filter';
import { BuyQueryResult } from '@core/type/sanity';

import { fetchSanityData } from '../../../helpers/make-server-props/helpers/sanity';
import {
  isRedirect,
  makeServerSideProps,
} from '../../../helpers/make-server-props/make-server-props';
import { ViewShop } from '../../../view';

export type ShopParams = {
  metal: EMetalSlug;
};

export type PageShopProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const PageShop = (props: PageShopProps) => {
  return useCMSPage<BuyQueryResult>({
    ...props,
    ViewPage: (pageProps) => (
      <ViewShop {...pageProps} filterResult={'cmsResult' in props && props.filterResult} />
    ),
  });
};

const getServerSideProps = makeServerSideProps<
  Required<Omit<DynamicSEOEntity, 'blogCategories'>> & {
    cmsResult: BuyQueryResult;
    pageId: string;
    filterResult: FilterProductsResult;
  },
  ShopParams,
  ShopQueryRaw & ShopParams,
  ERouting.BUY
>(ERouting.BUY, async (ctx) => {
  const res = await fetchSanityData<BuyQueryResult>(ctx);
  if (isRedirect(res)) {
    return res;
  }
  const cmsResult = res.props;

  await ctx.prefetch();
  const shopData = ctx.queryClient.getQueryData<PropsShop>([
    EQueryKey.SHOP,
    ctx.language,
    ctx.currency,
  ]);

  const page = Number(ctx.props.query.page) || 1;

  const shopQuery = getQuery(
    ctx.props.query,
    shopData?.filter,
    slugToMetal[ctx.language][ctx.props.params.metal],
  );
  const filterResult = filterProducts(shopData, ctx.language, ctx.props.query, shopQuery);

  if (page > 1 && filterResult.products.length === 0) {
    throw new Error('Not Found');
  }

  return {
    props: {
      cmsResult,
      pageId: cmsResult._id,
      metas: formatMetas(cmsResult.metadata),
      alternates: formatAlternates(cmsResult.alternates?.alt, ctx.keyRoute),
      breadcrumbs: cmsResult.body?.breadcrumbs
        ? mapSanityBreadcrumb(cmsResult.body.breadcrumbs)
        : [],
      filterResult,
    },
  };
});

export { getServerSideProps };

export default PageShop;
